
/**
 * Module dependencies.
 */

import { MouseEvent, ReactNode, ReactPortal, useCallback } from 'react';
import { canUseDOM } from '../../dom';
import { createPortal } from 'react-dom';
import { ifProp, theme } from 'styled-tools';
import BodyScroll from './body-scroll';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  isOpen: boolean;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isVisible: boolean }>`
  transition: visibility ${theme('animations.defaultTransition')};
  visibility: ${ifProp('isVisible', 'visible', 'hidden')};
`;

/**
 * Create react portal.
 */

function createReactPortal(children: ReactNode, elementId: string): null | ReactPortal {
  if (!canUseDOM()) {
    return null;
  }

  const domElement = document.getElementById(elementId);

  if (!domElement) {
    return null;
  }

  return createPortal(children, domElement);
}

/**
 * Export `ModalPortal` component.
 */

export function ModalPortal(props: Props): null | ReactPortal {
  const { children, isOpen } = props;
  const handleGhostClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (event) {
      event.stopPropagation();
    }
  }, []);

  return createReactPortal((
    <Wrapper
      isVisible={isOpen}
      onClick={handleGhostClick}
    >
      <BodyScroll enabled={!isOpen}>
        {children}
      </BodyScroll>
    </Wrapper>
  ), 'modal-root');
}
